/**
 * Analytics API calls (same Bearer + workspace headers as the workspace client).
 * Workspace id is injected when set via {@link setReportingApiWorkspaceId}.
 * Use ids from `/me` / workspace list only (Keycloak-aligned); see docs/SECURITY.md §2.6.
 */

import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { kongBrowserFetchUrl } from "@/lib/kongApi";

const REPORTING_PREFIX = "/api/v1/reporting";

// Analytics widgets are one-shot fetches. A transient introspection 401
// (e.g. token_validation_failed during introspection load) would otherwise
// leave a widget stuck on an error + manual Retry. Silently re-attempt a few
// times with backoff so intermittent blips recover on their own. The refresh
// coordinator dedupes/cools down refreshes, so this doesn't stampede.
const TRANSIENT_401_RETRIES = 2;
const TRANSIENT_401_BACKOFF_MS = [800, 1800];

let _workspaceId = null;

/** @param {string | null} id */
export function setReportingApiWorkspaceId(id) {
  _workspaceId = id && String(id).trim() ? String(id).trim() : null;
}

/**
 * @param {string} path - absolute path starting with /api/v1/reporting/...
 * @param {RequestInit} [init]
 */
export async function reportingApiFetch(path, init = {}) {
  const url = kongBrowserFetchUrl(path);
  const buildRequest = () => {
    const headers = {
      Accept: "application/json",
      ...(typeof window !== "undefined" ? getBearerAuthHeaders() : {}),
      ...(init.headers || {}),
    };
    if (_workspaceId) {
      headers["X-Workspace-Id"] = _workspaceId;
    }
    if (init.body && typeof init.body === "string" && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }
    return fetchWithTimeout(url, { ...init, headers });
  };

  // fetchWithAuthRetry already does refresh + single retry; on a definitive
  // session end it throws SessionExpiredError (propagates → modal). Here we add
  // bounded silent retries for a still-transient 401 so widgets self-heal.
  let res = await fetchWithAuthRetry(buildRequest);
  for (let attempt = 0; res.status === 401 && attempt < TRANSIENT_401_RETRIES; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, TRANSIENT_401_BACKOFF_MS[attempt] ?? 1800));
    res = await fetchWithAuthRetry(buildRequest);
  }

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    // Never surface internal auth codes (e.g. token_validation_failed) to the
    // widget UI. A lingering 401 after retries → a neutral, non-alarming message.
    if (res.status === 401) {
      const err = new Error("Metrics are temporarily unavailable.");
      err.status = 401;
      err.transient = true;
      throw err;
    }
    const msg = data?.detail?.error || data?.message || data?.error || res.statusText;
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg || "Request failed"));
  }
  return data;
}

export function reportingPath(suffix) {
  return `${REPORTING_PREFIX}${suffix.startsWith("/") ? suffix : `/${suffix}`}`;
}
